import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { BUSINESS } from "@/lib/api";

const headline = ["Detailing", "beyond", "perfection."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.83, 0, 0.17, 1], delay: 0.4 + i * 0.12 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.25, 0.7]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <section id="top" ref={ref} className="relative h-screen w-full overflow-hidden bg-background" data-testid="hero-section">
      <motion.div style={{ y: imgY }} className="absolute inset-0 z-0 scale-110">
        <img
          src="https://images.pexels.com/photos/3892898/pexels-photo-3892898.jpeg"
          alt="Luxury car detail"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-0 bg-black" />
      <div className="absolute inset-0 z-0 bg-gradient-to-t from-background via-transparent to-transparent" />

      <motion.div style={{ y: contentY }} className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-24 lg:pb-28">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="text-xs tracking-[0.3em] uppercase text-primary mb-6"
          data-testid="hero-overline"
        >
          {BUSINESS.location} — Est. Craftsmanship
        </motion.p>

        <h1 className="font-serif text-6xl sm:text-7xl lg:text-8xl xl:text-9xl leading-[0.9] tracking-tighter text-foreground max-w-5xl" data-testid="hero-headline">
          {headline.map((line, i) => (
            <span key={i} className="reveal-line">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block ${i === 1 ? "italic text-primary font-light pl-8 lg:pl-24" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row sm:items-end gap-8 sm:justify-between"
        >
          <p className="text-muted-foreground text-base lg:text-lg font-light max-w-md leading-relaxed">
            Obsessive interior detailing for the vehicles you love and the home you live in — restored to a flawless, showroom finish.
          </p>
          <div className="flex items-center gap-4 shrink-0">
            <a href="#book" data-testid="hero-book-btn" className="px-8 py-4 bg-primary text-primary-foreground text-sm tracking-wide hover:bg-primary/90 transition-colors clip-frame">
              Book a Detail
            </a>
            <a href="#services" data-testid="hero-services-btn" className="px-8 py-4 border border-white/25 text-foreground text-sm tracking-wide hover:border-primary hover:text-primary transition-colors clip-frame">
              Our Services
            </a>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 10, 0] }}
        transition={{ opacity: { delay: 1.8 }, y: { repeat: Infinity, duration: 2.2 } }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-primary/70"
      >
        <ArrowDown className="w-5 h-5" />
      </motion.div>
    </section>
  );
}
